import type { InterfaceLanguage } from '@/utils/languages'
import { Transform, Expose, Type } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, anyToDate } from './Models';

export type PreferredLanguage = 'fr' | 'en'

export class NotificationPreferences {
    @Expose()
    public assessment_completed_projects: string[] = []

    @Expose()
    public regulation_watch_projects: string[] = []

    @Expose()
    public preferred_language: PreferredLanguage = 'en'

    // When the next weekly recap is due. A send-scheduling cursor owned by the
    // backend cron, not a preference — every user is scheduled, and whether a
    // scheduled user is actually mailed is weekly_summary_disabled below
    // (MYAC-1199). The UI never writes it.
    @Expose()
    @Transform(anyToDate)
    public next_weekly_send_date: Date | null = null

    // Whether this user has been through the notification setup dialog.
    @Expose()
    public notification_setup_completed: boolean = false

    // Opt-OUT of the weekly summary. Negative on purpose: the summary is on by
    // default, so absent/false must mean "receives it". A positive `..._enabled`
    // would read better but any reader that missed the default would silently
    // switch everyone off.
    @Expose()
    public weekly_summary_disabled: boolean = false

    // The interface language the person chose in their profile (MYAC-1522).
    // It lives here because this block is what the person may write on their
    // user doc. null = the browser's language. Never the language of results:
    // that is the company's (Tenant.locale).
    @Expose()
    public interface_language: InterfaceLanguage | null = null

    /** MYAC-1522 — « Ne plus afficher à l'accueil »: the welcome letter is off for this person; the profile turns it back on. */
    @Expose()
    public welcome_letter_hidden: boolean = false

    /**
     * MYAC-1522 (onboarding pass, decision C) — the watch report email was asked in
     * the welcome letter (answered on or off there). Not `notification_setup_completed`:
     * the modal still asks the assessment emails once the company has a QMS or product.
     */
    @Expose()
    public watch_report_asked: boolean = false
}

export class User extends _FirebaseDocument {
    @Expose()
    public email: string

    @Expose()
    @Type(() => NotificationPreferences)
    public notification_preferences: NotificationPreferences = new NotificationPreferences()

    // MYAC-1522 — the name the person gives in « Faisons connaissance » or on the
    // profile. Absent until they give it; the greeting then says « Bonjour, » alone.
    @Expose()
    public first_name?: string | null

    @Expose()
    public last_name?: string | null

    collection(): string {
        return "users";
    }
    static fromFirebase(o: DocumentSnapshot): User | null {
        return _FirebaseDocument._fromFirebase(User, o);
    }
}

export class Invitation extends _FirebaseDocument {
    @Expose()
    public email: string
    @Expose()
    public tid: string
    @Expose()
    public pid: string
    @Expose()
    @Transform(anyToDate)
    public expiration_date: Date

    collection(): string {
        return "invitations";
    }
    static fromFirebase(o: DocumentSnapshot): Invitation | null {
        return _FirebaseDocument._fromFirebase(Invitation, o);
    }
}
